import React, { useState, useEffect } from "react";
import axios from "axios";
import "../styles/ForgotPassword.css";

export default function ForgotPassword({ onClose, email: initialEmail }) {
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordRules, setPasswordRules] = useState({
    minLength: false,
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSpecial: false,
  });

  const getChannel = (value) => {
    return value.includes("@") ? "email" : "phone";
  };

  const getApiErrorMessage = (err, fallbackMessage) => {
    const data = err?.response?.data;

    if (typeof data === "string") return data;
    if (data?.message) return data.message;

    return fallbackMessage;
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);

    setPasswordRules({
      minLength: value.length >= 6,
      hasUpper: /[A-ZÇĞİÖŞÜ]/.test(value),
      hasLower: /[a-zçğıöşü]/.test(value),
      hasNumber: /[0-9]/.test(value),
      hasSpecial: /[^a-zA-ZçğıöşüÇĞİÖŞÜ0-9]/.test(value),
    });
  };

  useEffect(() => {
    if (initialEmail) {
      setIdentifier(initialEmail);
    }
  }, [initialEmail]);

  const handleSendCode = async () => {
    const cleanIdentifier = identifier.trim();

    if (!cleanIdentifier) {
      setError("❗ E-posta veya telefon numarası giriniz.");
      return;
    }

    const channel = getChannel(cleanIdentifier);

    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/Auth/forgotpassword`,
        {
          identifier: cleanIdentifier,
          channel,
        }
      );

      setMessage(
        channel === "phone"
          ? "📲 Kod telefon numaranıza SMS olarak gönderildi."
          : "📨 Kod e-posta adresinize gönderildi."
      );
      setError("");
      setStep(2);
    } catch (err) {
      setError(
        getApiErrorMessage(
          err,
          "❌ E-posta/telefon bulunamadı veya doğrulanmamış."
        )
      );
      setMessage("");
    } finally {
      setLoading(false);
    }
  };

  const handleContinueWithCode = async () => {
    const cleanIdentifier = identifier.trim();

    if (!code) {
      setError("❗ Kod alanı boş olamaz.");
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/Auth/verify-reset-code`,
        {
          identifier: cleanIdentifier,
          code,
        }
      );

      setStep(3);
      setError("");
      setMessage("✅ Kod doğrulandı.");
    } catch (err) {
      setError(getApiErrorMessage(err, "❌ Kod hatalı veya süresi dolmuş."));
      setMessage("");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const cleanIdentifier = identifier.trim();

    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/Auth/resetpassword`, {
        identifier: cleanIdentifier,
        code,
        newPassword,
        newPasswordConfirm,
      });

      setMessage("✅ Şifren başarıyla sıfırlandı.");
      setError("");
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      setError(
        getApiErrorMessage(err, "❌ Kod geçersiz veya şifreler uyuşmuyor.")
      );
      setMessage("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-popup-forgot">
        <button className="sifre-close-btn" onClick={onClose}>×</button>
        <h3>Şifremi Unuttum</h3>

        {step === 1 && (
          <>
            <input
              type="text"
              placeholder="E-posta veya telefon no. Örn:5551234567"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoComplete="username"
            />


            <button onClick={handleSendCode} disabled={loading}>
              {loading ? "Gönderiliyor..." : "Kod Gönder"}
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <input
              type="text"
              placeholder="Gelen doğrulama kodunu girin"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <button onClick={handleContinueWithCode} disabled={loading}>
              {loading ? "Doğrulanıyor..." : "Devam Et"}
            </button>
          </>
        )}

        {step === 3 && (
          <>
            <div className="password-input-wrapper">
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={handlePasswordChange}
                required
                placeholder="Yeni şifre"
              />
              <span
                className="password-toggle"
                onClick={() => setShowNewPassword((prev) => !prev)}
              >
                {showNewPassword ? "Gizle" : "Göster"}
              </span>
            </div>

            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Yeni şifre (tekrar)"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                className="password-input"
              />
              <span
                className="password-toggle"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
              >
                {showConfirmPassword ? "Gizle" : "Göster"}
              </span>
            </div>

            <div className="password-rules">
              <div className={passwordRules.minLength ? "rule passed" : "rule"}>
                {passwordRules.minLength ? "✔️" : "⭕"} En az 6 karakter
              </div>
              <div className={passwordRules.hasUpper ? "rule passed" : "rule"}>
                {passwordRules.hasUpper ? "✔️" : "⭕"} Büyük harf (A-Z)
              </div>
              <div className={passwordRules.hasLower ? "rule passed" : "rule"}>
                {passwordRules.hasLower ? "✔️" : "⭕"} Küçük harf (a-z)
              </div>
              <div className={passwordRules.hasNumber ? "rule passed" : "rule"}>
                {passwordRules.hasNumber ? "✔️" : "⭕"} Rakam (0-9)
              </div>
              <div className={passwordRules.hasSpecial ? "rule passed" : "rule"}>
                {passwordRules.hasSpecial ? "✔️" : "⭕"} Özel karakter (!@#...)
              </div>
            </div>

            <button onClick={handleResetPassword} disabled={loading}>
              {loading ? "Sıfırlanıyor..." : "Şifreyi Sıfırla"}
            </button>
          </>
        )}

        {message && <p className="success">{message}</p>}
        {error && <p className="error">{error}</p>}
      </div>
    </div>
  );
}